import React, { useEffect, useMemo, useRef, useState } from "react"
import { Asset, Link } from "../../../../plugins/gatsby-plugin-atollon"
import StructuredText from "../custom-structured-text-renderer"
import { graphql } from "gatsby"
import { EdgeTop1, Close } from "../../icons"

const BurgerColumnItem = ({ title, items, theme }) => {
  const [popup, setPopup] = useState(false)
  const [video, setVideo] = useState(items?.[0]?.video || null)
  const [openNonce, setOpenNonce] = useState(0) // force iframe remount per open

  const wrapperRef = useRef(null)

  // Lock/unlock body scroll when popup toggles
  useEffect(() => {
    if (!popup) return
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = prev
    }
  }, [popup])

  // Close on outside click while popup is open
  useEffect(() => {
    if (!popup) return

    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setPopup(false)
        setVideo(null) // fully unmount iframe for Safari
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [popup])

  // Safely extract a YouTube ID from various URL shapes
  const videoId = useMemo(() => {
    const urlString = video?.url ?? ""
    if (!urlString) return ""
    try {
      const u = new URL(urlString)
      const host = u.hostname.toLowerCase()
      const path = u.pathname
      const params = u.searchParams

      if (host.includes("youtu.be")) return path.slice(1)
      if (params.has("v")) return params.get("v")
      if (path.startsWith("/embed/")) return path.split("/embed/")[1]
      if (path.startsWith("/shorts/")) return path.split("/shorts/")[1].split(/[/?]/)[0]
    } catch {
      // fallback regex if not a valid URL
      const m = urlString.match(
        /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{11})/
      )
      if (m) return m[1]
    }
    return ""
  }, [video])

  // Build embed URL (autoplay/mute/playsinline help Safari)
  const baseEmbed = videoId ? `https://www.youtube-nocookie.com/embed/${videoId}` : ""
  const autoplayParams = "autoplay=1&mute=1&playsinline=1&rel=0"
  const videoSrc = popup && baseEmbed ? `${baseEmbed}?${autoplayParams}` : baseEmbed

  return (
    <>
      <section
        className={`three-column-item three-column-item--burger three-column-item--ambassadors ${theme || ""}`}
      >
        {theme === "woods" && (
          <div className="three-column-item__edge">
            <EdgeTop1 color="#FFFDED" />
          </div>
        )}

        <div className="three-column-item__inner">
          { title && <h2>{title}</h2> }
          <div className={`three-column-item__items${items?.length === 4 ? " three-column-item__items--four" : ""}`}>
            {items?.map((el, i) => (
              <div
                key={i}
                className="three-column-item__item"
                data-sal="slide-up"
                data-sal-duration="1000"
                data-sal-easing="ease"
              >
                {el.logo && (
                  <div className="three-column-item__logo">
                    <Asset asset={el.logo} />
                  </div>
                )}
                {el.image && <Asset asset={el.image} />}
                {el.name && <h3>{el.name}</h3>}
                {el.restaurant && (
                  <h4>
                    {el.restaurant}
                    <br />
                    {el.location}
                  </h4>
                )}

                <button
                  className="btn btn--gold"
                  onClick={() => {
                    setVideo(el.video || null)
                    setOpenNonce((n) => n + 1) // new key each open so Safari doesn’t reuse
                    setPopup(true)
                  }}
                >
                  <span>Watch</span>
                  <svg
                    width="25"
                    height="25"
                    viewBox="0 0 25 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6.5 4.83329V20.8333C6.49995 21.0112 6.54737 21.1859 6.63738 21.3394C6.72739 21.4929 6.85672 21.6196 7.01202 21.7064C7.16733 21.7933 7.34299 21.8371 7.52088 21.8334C7.69878 21.8296 7.87245 21.7785 8.024 21.6853L21.024 13.6853C21.1696 13.5958 21.2898 13.4705 21.3733 13.3214C21.4567 13.1722 21.5005 13.0042 21.5005 12.8333C21.5005 12.6624 21.4567 12.4944 21.3733 12.3452C21.2898 12.196 21.1696 12.0708 21.024 11.9813L8.024 3.98129C7.87245 3.88805 7.69878 3.83694 7.52088 3.83323C7.34299 3.82951 7.16733 3.87333 7.01202 3.96015C6.85672 4.04698 6.72739 4.17367 6.63738 4.32715C6.54737 4.48064 6.49995 4.65536 6.5 4.83329Z"
                      fill="white"
                    />
                  </svg>
                </button>

                {el.richContent && <StructuredText data={el.richContent} />}
              </div>
            ))}
          </div>
          {theme === "woods" && (
            <div className="three-column-item__buttons">
              <Link className="btn btn--green">
                <span>Meet our Ambassardors</span>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Popup */}
      <div className={`popup ${popup ? "popup--active" : ""}`}>
        <div className="popup__wrapper" ref={wrapperRef}>
          <button
            className="popup__close"
            onClick={() => {
              setPopup(false)
              setVideo(null) // tear down iframe so Safari won’t reuse it
            }}
          >
            <Close />
          </button>

          <div className="popup__content">
            {popup && videoId && (
              <iframe
                key={`${videoId}-${openNonce}`} // hard remount every open
                src={videoSrc}
                title="Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                // iOS/Safari hint
                playsInline
              />
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default BurgerColumnItem

export const query = graphql`
  fragment BurgerColumnItemProps on DatoCmsBurgerColumnItem {
    ... on DatoCmsBurgerColumnItem {
      id
      title
      items {
        logo {
          ...InternalAssetProps
        }
        image {
          ...InternalAssetProps
        }
        name
        restaurant
        location
        richContent {
          value
        }
        video {
          url
        }
      }
    }
  }
`
